import { DeleteOutlined, DownloadOutlined, EditOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import { AutoComplete, Button, Divider, Form, Modal, Select, Space, Spin, Table, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { FilterValue, SorterResult, TablePaginationConfig } from "antd/es/table/interface";
import { useCallback, useEffect, useMemo, useState } from "react";
import { clientFetch } from "../../utils/clientFetch";
import { useTranslation } from "react-i18next";
import { CsvImportModalButton } from "../../components/common/CsvImportModalButton";
import { parseReportLayoutMasterCsvErrorPayload, showReportLayoutMasterCsvErrorModal } from "./reportLayoutMasterCsvErrorModal";

const CLOTHTYPE_MAX_LENGTH = 255;
const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 20;
const CSV_FORMULA_PREFIXES = new Set(["=", "+", "-", "@"]);
const REPORT_LAYOUT_MASTER_TEMPLATE_URL = `${process.env.PUBLIC_URL}/csv-templates/report_layout_masters.csv`;
const REPORT_LAYOUT_MASTER_GUIDE_URL = `${process.env.PUBLIC_URL}/csv-guides/report-layout-master-import.md`;

const REPORT_LAYOUT_OPTIONS = [
  { value: 1, labelKey: "reportLayout.bottomsLayout" },
  { value: 2, labelKey: "reportLayout.topsLayout" },
  { value: 3, labelKey: "reportLayout.otherLayout" },
] as const;

type ReportLayoutType = typeof REPORT_LAYOUT_OPTIONS[number]["value"];

type ReportLayoutMaster = {
  id: number;
  clothtype: string;
  layout_type: ReportLayoutType;
  updated_at: string;
};

type ReportLayoutMasterMeta = {
  total: number;
  page: number;
  limit: number;
};

type ReportLayoutMasterResponse = {
  data?: unknown;
  meta?: Partial<ReportLayoutMasterMeta>;
};

type ClothtypeHistoryResponse = {
  data?: unknown;
};

type ReportLayoutMasterSortBy = "clothtype" | "layout_type" | "updated_at";

type ReportLayoutMasterSortState = {
  sortField?: ReportLayoutMasterSortBy;
  sortOrder?: "ascend" | "descend";
};

type ReportLayoutMasterFilterState = {
  layoutType?: ReportLayoutType;
};

type ReportLayoutMasterFetchParams = {
  page: number;
  limit: number;
  sortField?: ReportLayoutMasterSortBy;
  sortOrder?: "asc" | "desc";
  layoutType?: ReportLayoutType;
};

type ReportLayoutMasterReloadParams = {
  page?: number;
  limit?: number;
  sortField?: ReportLayoutMasterSortBy;
  sortOrder?: "ascend" | "descend";
  layoutType?: ReportLayoutType;
};

type ReportLayoutMasterFormValue = {
  clothtype: string;
  layout_type: ReportLayoutType;
};

type CsvUploadRequestOption = {
  file: string | Blob;
  onSuccess?: (body: unknown) => void;
  onError?: (error: Error) => void;
};

function getApiEndpoint(path: string) {
  return `${process.env.REACT_APP_API_ENDPOINT}/apis/admin${path}`;
}

function isReportLayoutMaster(value: unknown): value is ReportLayoutMaster {
  if (!value || typeof value !== "object") {
    return false;
  }

  const record = value as Record<string, unknown>;
  return typeof record.id === "number"
    && typeof record.clothtype === "string"
    && typeof record.layout_type === "number"
    && REPORT_LAYOUT_OPTIONS.some((option) => option.value === record.layout_type);
}

function parseReportLayoutMasters(value: unknown) {
  if (!Array.isArray(value)) {
    return [];
  }

  return value.filter(isReportLayoutMaster);
}

function parseReportLayoutMasterResponse(value: unknown): { items: ReportLayoutMaster[]; meta: ReportLayoutMasterMeta } {
  if (!value || typeof value !== "object") {
    return { items: [], meta: { total: 0, page: DEFAULT_PAGE, limit: DEFAULT_PAGE_SIZE } };
  }

  const response = value as ReportLayoutMasterResponse;
  const total = typeof response.meta?.total === "number" ? response.meta.total : 0;
  const page = typeof response.meta?.page === "number" ? response.meta.page : DEFAULT_PAGE;
  const limit = typeof response.meta?.limit === "number" ? response.meta.limit : DEFAULT_PAGE_SIZE;

  return {
    items: parseReportLayoutMasters(response.data),
    meta: { total, page, limit },
  };
}

function parseClothtypeHistoryOptions(value: unknown) {
  if (!value || typeof value !== "object") {
    return [];
  }

  const response = value as ClothtypeHistoryResponse;
  if (!Array.isArray(response.data)) {
    return [];
  }

  return response.data
    .filter((record): record is { clothtype: string } => (
      typeof record === "object"
      && record !== null
      && typeof (record as Record<string, unknown>).clothtype === "string"
      && ((record as Record<string, string>).clothtype).trim() !== ""
    ))
    .map((record) => ({
      value: record.clothtype,
      label: record.clothtype,
    }));
}

function containsControlCharacter(value: string) {
  return Array.from(value).some((character) => {
    const codePoint = character.codePointAt(0);
    return codePoint !== undefined && (codePoint <= 31 || codePoint === 127);
  });
}

function startsWithCsvFormulaPrefix(value: string) {
  return CSV_FORMULA_PREFIXES.has(value.trimStart()[0]);
}

function isReportLayoutType(value: unknown): value is ReportLayoutType {
  return typeof value === "number" && REPORT_LAYOUT_OPTIONS.some((option) => option.value === value);
}

export function ReportLayoutMasterPage() {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm<ReportLayoutMasterFormValue>();
  const [masters, setMasters] = useState<ReportLayoutMaster[]>([]);
  const [editingMaster, setEditingMaster] = useState<ReportLayoutMaster | null>(null);
  const [pagination, setPagination] = useState<ReportLayoutMasterMeta>({
    total: 0,
    page: DEFAULT_PAGE,
    limit: DEFAULT_PAGE_SIZE,
  });
  const [sortState, setSortState] = useState<ReportLayoutMasterSortState>({});
  const [filterState, setFilterState] = useState<ReportLayoutMasterFilterState>({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [clothtypeOptions, setClothtypeOptions] = useState<Array<{ value: string; label: string }>>([]);
  const [isLoadingClothtypes, setIsLoadingClothtypes] = useState(false);

  const toApiSortOrder = useCallback((value?: "ascend" | "descend") => {
    if (value === "ascend") {
      return "asc";
    }

    if (value === "descend") {
      return "desc";
    }

    return undefined;
  }, []);

  const fetchMasters = useCallback(async (nextParams: ReportLayoutMasterFetchParams) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(nextParams.page),
        limit: String(nextParams.limit),
      });
      if (nextParams.sortField && nextParams.sortOrder) {
        params.set("sortField", nextParams.sortField);
        params.set("sortOrder", nextParams.sortOrder);
      }
      if (nextParams.layoutType !== undefined) {
        params.set("layout_type", String(nextParams.layoutType));
      }
      const response = await clientFetch(getApiEndpoint(`/report-layout-masters?${params.toString()}`), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("採寸報告書レイアウトマスタの取得に失敗しました。");
      }

      const data = await response.json();
      const parsed = parseReportLayoutMasterResponse(data);
      setMasters(parsed.items);
      setPagination(parsed.meta);
    } catch (error) {
      console.error("採寸報告書レイアウトマスタの取得に失敗しました。", error);
      message.error(t("reportLayout.fetchFailed"));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const reloadMasters = useCallback((params: ReportLayoutMasterReloadParams = {}) => {
    const nextSortField = params.sortField ?? sortState.sortField;
    const nextSortOrder = params.sortOrder ?? sortState.sortOrder;
    return fetchMasters({
      page: params.page ?? pagination.page,
      limit: params.limit ?? pagination.limit,
      sortField: nextSortField,
      sortOrder: toApiSortOrder(nextSortOrder),
      layoutType: params.layoutType ?? filterState.layoutType,
    });
  }, [fetchMasters, filterState.layoutType, pagination.limit, pagination.page, sortState.sortField, sortState.sortOrder, toApiSortOrder]);

  const fetchClothtypeOptions = useCallback(async (keyword = "") => {
    setIsLoadingClothtypes(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      const trimmedKeyword = keyword.trim();
      if (trimmedKeyword !== "") {
        params.set("keyword", trimmedKeyword);
      }

      const response = await clientFetch(getApiEndpoint(`/clothtype-histories?${params.toString()}`), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error("識別の種類候補の取得に失敗しました。");
      }

      const data = await response.json();
      setClothtypeOptions(parseClothtypeHistoryOptions(data));
    } catch (error) {
      console.error("識別の種類候補の取得に失敗しました。", error);
      message.error("識別の種類候補を取得できませんでした。");
    } finally {
      setIsLoadingClothtypes(false);
    }
  }, []);

  useEffect(() => {
    fetchMasters({
      page: DEFAULT_PAGE,
      limit: DEFAULT_PAGE_SIZE,
      sortOrder: "asc",
    });
    // 初期表示だけで取得するため、以後はTable操作や保存・削除処理から明示的に取得する。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMasters]);

  const openCreateModal = useCallback(() => {
    setEditingMaster(null);
    form.setFieldsValue({ clothtype: "", layout_type: 3 });
    fetchClothtypeOptions();
    setIsModalOpen(true);
  }, [fetchClothtypeOptions, form]);

  const openEditModal = useCallback((master: ReportLayoutMaster) => {
    setEditingMaster(master);
    form.setFieldsValue({
      clothtype: master.clothtype,
      layout_type: master.layout_type,
    });
    fetchClothtypeOptions(master.clothtype);
    setIsModalOpen(true);
  }, [fetchClothtypeOptions, form]);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingMaster(null);
    form.resetFields();
  }, [form]);

  const saveMaster = useCallback(async () => {
    let values: ReportLayoutMasterFormValue;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    setIsSaving(true);
    try {
      const path = editingMaster
        ? `/report-layout-masters/${editingMaster.id}`
        : "/report-layout-masters";
      const response = await clientFetch(getApiEndpoint(path), {
        method: editingMaster ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clothtype: values.clothtype.trim(),
          layout_type: values.layout_type,
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        const messageText = !i18n.language.startsWith("en") && typeof payload.message === "string"
          ? payload.message
          : t("reportLayout.saveFailed");
        throw new Error(messageText);
      }

      message.success(t("reportLayout.saveSucceeded"));
      closeModal();
      reloadMasters();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : t("reportLayout.saveFailed");
      message.error(errorMessage);
    } finally {
      setIsSaving(false);
    }
  }, [closeModal, editingMaster, form, reloadMasters, t]);

  const deleteMaster = useCallback((master: ReportLayoutMaster) => {
    Modal.confirm({
      title: t("reportLayout.deleteConfirmTitle"),
      content: t("reportLayout.deleteConfirmContent", { name: master.clothtype }),
      okText: t("common.delete"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      centered: true,
      async onOk() {
        try {
          const response = await clientFetch(getApiEndpoint(`/report-layout-masters/${master.id}`), {
            method: "DELETE",
          });

          if (!response.ok) {
            throw new Error(t("reportLayout.deleteFailed"));
          }

          message.success(t("reportLayout.deleteSucceeded"));
          const totalAfterDelete = Math.max(pagination.total - 1, 0);
          const maxPage = Math.max(1, Math.ceil(totalAfterDelete / pagination.limit));
          await reloadMasters({ page: Math.min(pagination.page, maxPage) });
        } catch (error) {
          console.error("採寸報告書レイアウトマスタの削除に失敗しました。", error);
          message.error(t("reportLayout.deleteFailed"));
        }
      },
    });
  }, [pagination, reloadMasters, t]);

  const downloadCsv = useCallback(async () => {
    try {
      const response = await clientFetch(getApiEndpoint("/csv/download/report-layout-masters"), {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (!response.ok) {
        throw new Error(t("messages.csvDownloadFailed"));
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "report_layout_masters.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("採寸報告書レイアウトマスタCSVのダウンロードに失敗しました。", error);
      message.error(t("messages.csvDownloadFailed"));
    }
  }, [t]);

  const uploadCsv = useCallback(async (options: CsvUploadRequestOption) => {
    const { file, onSuccess, onError } = options;
    setIsUploading(true);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await clientFetch(getApiEndpoint("/csv/upload/report-layout-masters"), {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const payload = await parseReportLayoutMasterCsvErrorPayload(response);
        showReportLayoutMasterCsvErrorModal(file instanceof File ? file.name : "report_layout_masters.csv", payload);
        onError?.(new Error(t("messages.csvUploadFailed")));
        return;
      }

      const result = await response.json();
      onSuccess?.(result);
      message.success(t("reportLayout.csvUploadSucceeded"));
      reloadMasters({ page: DEFAULT_PAGE });
    } catch (error) {
      console.error("採寸報告書レイアウトマスタCSVのアップロードに失敗しました。", error);
      showReportLayoutMasterCsvErrorModal(file instanceof File ? file.name : "report_layout_masters.csv");
      onError?.(error instanceof Error ? error : new Error(t("messages.csvUploadFailed")));
    } finally {
      setIsUploading(false);
    }
  }, [reloadMasters, t]);

  const reportLayoutOptions = useMemo(() => REPORT_LAYOUT_OPTIONS.map((option) => ({
    value: option.value,
    label: t(option.labelKey),
  })), [t]);

  const columns = useMemo<ColumnsType<ReportLayoutMaster>>(() => [
    {
      title: t("reportLayout.identifier"),
      dataIndex: "clothtype",
      key: "clothtype",
      sorter: true,
      sortOrder: sortState.sortField === "clothtype" ? sortState.sortOrder : undefined,
    },
    {
      title: t("reportLayout.layoutType"),
      dataIndex: "layout_type",
      key: "layout_type",
      render: (value: ReportLayoutType) => reportLayoutOptions.find((option) => option.value === value)?.label ?? value,
      filters: reportLayoutOptions.map((option) => ({ text: option.label, value: option.value })),
      filteredValue: filterState.layoutType !== undefined ? [filterState.layoutType] : null,
    },
    {
      title: t("reportLayout.updatedAt"),
      dataIndex: "updated_at",
      key: "updated_at",
      sorter: true,
      sortOrder: sortState.sortField === "updated_at" ? sortState.sortOrder : undefined,
      render: (value: string) => value ? new Date(value).toLocaleString(i18n.language === "en" ? "en-US" : "ja-JP") : "",
    },
    {
      title: t("common.operation"),
      key: "actions",
      width: 160,
      render: (_, record) => (
        <Space>
          <Button variant="outlined" color="primary" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
            {t("common.edit")}
          </Button>
          <Button danger icon={<DeleteOutlined />} onClick={() => deleteMaster(record)}>
            {t("common.delete")}
          </Button>
        </Space>
      ),
    },
  ], [deleteMaster, filterState.layoutType, i18n.language, openEditModal, reportLayoutOptions, sortState.sortField, sortState.sortOrder, t]);

  const handleTableChange = useCallback((
    nextPagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<ReportLayoutMaster> | SorterResult<ReportLayoutMaster>[]
  ) => {
    const sortInfo = Array.isArray(sorter) ? sorter[0] : sorter;
    const sortField = sortInfo?.field;
    const nextSortField: ReportLayoutMasterSortBy | undefined =
      sortInfo?.order && (sortField === "layout_type" || sortField === "updated_at" || sortField === "clothtype")
        ? sortField
        : undefined;
    const nextSortOrder = sortInfo?.order === "ascend" || sortInfo?.order === "descend"
      ? sortInfo.order
      : undefined;
    const layoutTypeFilter = filters.layout_type?.[0];
    const nextLayoutType = isReportLayoutType(layoutTypeFilter) ? layoutTypeFilter : undefined;
    const hasSortChanged = sortState.sortField !== nextSortField || sortState.sortOrder !== nextSortOrder;
    const hasFilterChanged = filterState.layoutType !== nextLayoutType;
    const nextPage = hasSortChanged || hasFilterChanged
      ? DEFAULT_PAGE
      : nextPagination.current ?? pagination.page;

    setSortState({
      sortField: nextSortField,
      sortOrder: nextSortOrder,
    });
    setFilterState({ layoutType: nextLayoutType });

    fetchMasters({
      page: nextPage,
      limit: nextPagination.pageSize ?? pagination.limit,
      sortField: nextSortField,
      sortOrder: toApiSortOrder(nextSortOrder),
      layoutType: nextLayoutType,
    });
  }, [fetchMasters, filterState.layoutType, pagination, sortState.sortField, sortState.sortOrder, toApiSortOrder]);

  return (
    <div className="flex flex-col h-full">
      <Spin spinning={isUploading} tip={t("common.uploading")} size="large" fullscreen />
      <div className="flex w-full items-center justify-between">
        <p className="flex text-2xl decoration-solid ml-2 mt-0 mb-0 border-0 border-l-4 border-solid border-[#1677ff] p-2">
          {t("reportLayout.title")}
        </p>
        <Space>
          <Button type="primary" icon={<DownloadOutlined />} onClick={downloadCsv} disabled={isUploading}>
            {t("common.downloadCsv")}
          </Button>
          <CsvImportModalButton
            title={t("reportLayout.csvImportTitle")}
            templateUrl={REPORT_LAYOUT_MASTER_TEMPLATE_URL}
            templateFileName="report_layout_masters_template.csv"
            guideUrl={REPORT_LAYOUT_MASTER_GUIDE_URL}
            uploadRequest={uploadCsv}
            disabled={isUploading}
            uploading={isUploading}
            buttonText={t("common.uploadCsv")}
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            {t("reportLayout.create")}
          </Button>
        </Space>
      </div>
      <Divider />
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={masters}
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total: pagination.total,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
      />
      <Modal
        title={editingMaster ? t("reportLayout.editTitle") : t("reportLayout.registerTitle")}
        open={isModalOpen}
        onCancel={closeModal}
        onOk={saveMaster}
        okText={t("common.save")}
        cancelText={t("common.cancel")}
        confirmLoading={isSaving}
        okButtonProps={{ icon: <SaveOutlined /> }}
        centered
      >
        <Form form={form} layout="vertical" initialValues={{ layout_type: 3 }}>
          <Form.Item
            label={t("reportLayout.identifier")}
            name="clothtype"
            rules={[
              { required: true, message: t("reportLayout.validation.identifierRequired") },
              { max: CLOTHTYPE_MAX_LENGTH, message: t("reportLayout.validation.maxLength", { max: CLOTHTYPE_MAX_LENGTH }) },
              {
                validator: (_, value: string | undefined) => {
                  if (!value || !containsControlCharacter(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("reportLayout.validation.noControlCharacters")));
                },
              },
              {
                validator: (_, value: string | undefined) => {
                  if (!value || !startsWithCsvFormulaPrefix(value)) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t("reportLayout.validation.noCsvFormulaPrefix")));
                },
              },
            ]}
          >
            <AutoComplete
              allowClear
              maxLength={CLOTHTYPE_MAX_LENGTH}
              options={clothtypeOptions}
              onSearch={fetchClothtypeOptions}
              placeholder="識別の種類を入力または選択"
              notFoundContent={isLoadingClothtypes ? "読み込み中..." : null}
            />
          </Form.Item>
          <Form.Item
            label={t("reportLayout.layoutType")}
            name="layout_type"
            rules={[{ required: true, message: t("reportLayout.validation.layoutTypeRequired") }]}
          >
            <Select options={reportLayoutOptions} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
